import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useMemo, useRef } from 'react';
import { BUTTON_SIZE_MAP, useButtonNavigation, useClearButton } from '@snack-uikit/input-private';
import { isAccordionItemProps, isNextListItemProps } from '@snack-uikit/list';
import { useValueControl } from '@snack-uikit/utils';
import { ButtonCopyValue } from '../../helperComponents';
import { extractChildIds } from './legacy';
import { isBaseOptionProps } from './utils';
import { jsx as _jsx } from "react/jsx-runtime";
export function useHandleOnKeyDown(_ref) {
  var setOpen = _ref.setOpen,
    inputKeyDownNavigationHandler = _ref.inputKeyDownNavigationHandler,
    onInputKeyDownProp = _ref.onInputKeyDownProp;
  return useCallback(function (onKeyDown) {
    return function (e) {
      if (e.code === 'Space') {
        e.stopPropagation();
      } else {
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
      }
      if (e.code === 'ArrowUp') {
        setOpen(false);
      }
      if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
      }
      if (e.key === 'Tab') {
        setOpen(false);
      }
      inputKeyDownNavigationHandler(e);
      onInputKeyDownProp === null || onInputKeyDownProp === void 0 ? void 0 : onInputKeyDownProp(e);
    };
  }, [inputKeyDownNavigationHandler, onInputKeyDownProp, setOpen]);
}
export function useCopyButton(_ref2) {
  var copyButtonRef = _ref2.copyButtonRef,
    showCopyButton = _ref2.showCopyButton,
    size = _ref2.size,
    valueToCopy = _ref2.valueToCopy,
    onValueRequest = _ref2.onValueRequest,
    disabled = _ref2.disabled,
    _ref2$prefix = _ref2.prefix,
    prefix = _ref2$prefix === void 0 ? '' : _ref2$prefix,
    _ref2$postfix = _ref2.postfix,
    postfix = _ref2$postfix === void 0 ? '' : _ref2$postfix;
  return useMemo(function () {
    return {
      id: 'copy',
      active: true,
      ref: copyButtonRef,
      show: showCopyButton,
      render: function render(props) {
        return /*#__PURE__*/_jsx(ButtonCopyValue, _objectSpread(_objectSpread({}, props), {}, {
          size: BUTTON_SIZE_MAP[size],
          valueToCopy: (prefix !== null && prefix !== void 0 ? prefix : '') + valueToCopy + (postfix !== null && postfix !== void 0 ? postfix : ''),
          onValueRequest: onValueRequest,
          disabled: disabled
        }));
      }
    };
  }, [copyButtonRef, disabled, onValueRequest, showCopyButton, size, valueToCopy, prefix, postfix]);
}
export function useButtons(_ref3) {
  var readonly = _ref3.readonly,
    showClearButton = _ref3.showClearButton,
    showCopyButton = _ref3.showCopyButton,
    size = _ref3.size,
    onClear = _ref3.onClear,
    inputRef = _ref3.inputRef,
    _ref3$valueToCopy = _ref3.valueToCopy,
    valueToCopy = _ref3$valueToCopy === void 0 ? '' : _ref3$valueToCopy;
  var clearButtonRef = useRef(null);
  var copyButtonRef = useRef(null);
  var buttonsRefs = [copyButtonRef.current, clearButtonRef.current];
  var clearButtonSettings = useClearButton({
    clearButtonRef: clearButtonRef,
    showClearButton: !readonly && showClearButton,
    size: size,
    onClear: onClear
  });
  var copyButtonSettings = useCopyButton({
    copyButtonRef: copyButtonRef,
    showCopyButton: readonly && showCopyButton,
    size: size,
    valueToCopy: valueToCopy
  });
  var _useButtonNavigation = useButtonNavigation({
      inputRef: inputRef,
      postfixButtons: useMemo(function () {
        return [clearButtonSettings, copyButtonSettings];
      }, [clearButtonSettings, copyButtonSettings]),
      onButtonKeyDown: undefined,
      readonly: readonly,
      submitKeys: ['Enter', 'Space', 'Tab']
    }),
    inputKeyDownNavigationHandler = _useButtonNavigation.onInputKeyDown,
    postfixButtons = _useButtonNavigation.postfixButtons;
  return {
    postfixButtons: postfixButtons,
    inputKeyDownNavigationHandler: inputKeyDownNavigationHandler,
    buttonsRefs: buttonsRefs
  };
}
export function useSearchInput(_ref4) {
  var value = _ref4.value,
    onChange = _ref4.onChange,
    defaultValue = _ref4.defaultValue,
    selectedOptionFormatter = _ref4.selectedOptionFormatter;
  var _useValueControl = useValueControl({
      value: value,
      onChange: onChange,
      defaultValue: defaultValue
    }),
    _useValueControl2 = _slicedToArray(_useValueControl, 2),
    _useValueControl2$ = _useValueControl2[0],
    inputValue = _useValueControl2$ === void 0 ? '' : _useValueControl2$,
    setInputValue = _useValueControl2[1];
  var prevInputValue = useRef(inputValue);
  var updateInputValue = useCallback(function (selectedItem) {
    var newInputValue = selectedOptionFormatter(selectedItem);
    if (inputValue !== newInputValue || prevInputValue.current !== newInputValue) {
      setInputValue(newInputValue);
      prevInputValue.current = newInputValue;
    }
  }, [inputValue, selectedOptionFormatter, setInputValue]);
  return {
    inputValue: inputValue,
    setInputValue: setInputValue,
    prevInputValue: prevInputValue,
    onInputValueChange: setInputValue,
    updateInputValue: updateInputValue
  };
}
export function useHandleDeleteItem(setValue) {
  return useCallback(function (item) {
    return function (e) {
      e === null || e === void 0 ? void 0 : e.stopPropagation();
      if (!item) {
        return;
      }
      if (isAccordionItemProps(item) || isNextListItemProps(item)) {
        var _item$id;
        var removeIds = extractChildIds({
          items: item.items
        }).concat((_item$id = item.id) !== null && _item$id !== void 0 ? _item$id : '');
        setValue(function (value) {
          return value === null || value === void 0 ? void 0 : value.filter(function (v) {
            return !removeIds.includes(v !== null && v !== void 0 ? v : '');
          });
        });
        return;
      }
      if (isBaseOptionProps(item)) {
        setValue(function (value) {
          return value === null || value === void 0 ? void 0 : value.filter(function (v) {
            return (
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              v !== item.id
            );
          });
        });
      }
    };
  }, [setValue]);
}